import { type ImporterTask } from '~/store/tasks/types';

export type ImportBatchOfferTask = Pick<ImporterTask, 'createdAt' | 'remoteTaskId' | 'status' | 'updatedAt'>;

export type FinishedImportTask = {
  createdAt: number;
  /** `markTaskAsCompletedAction` stamps `updatedAt` when the completion lands, in ms. */
  finishedAt: number;
  taskId: string;
};

const isImportTaskInProgress = (status: ImporterTask['status']): boolean =>
  status === 'pending' || status === 'in-progress';

const toTime = (value: string | undefined): number => {
  const time = value ? Date.parse(value) : Number.NaN;

  return Number.isNaN(time) ? -Infinity : time;
};

/** Tasks persist to localStorage, so a completed import restored on reload was never watched here. */
export const rememberWatchedImportTasks = (watched: Set<string>, tasks: readonly ImportBatchOfferTask[]): void => {
  for (const task of tasks) {
    if (task.remoteTaskId && isImportTaskInProgress(task.status)) {
      watched.add(task.remoteTaskId);
    }
  }
};

export const getLatestFinishedImportTask = (
  tasks: readonly ImportBatchOfferTask[],
  watchedTaskIds: ReadonlySet<string>,
): FinishedImportTask | null => {
  let latest: ImportBatchOfferTask | null = null;

  for (const task of tasks) {
    if (task.remoteTaskId && (latest === null || toTime(task.createdAt) > toTime(latest.createdAt))) {
      latest = task;
    }
  }

  if (!latest?.remoteTaskId || !watchedTaskIds.has(latest.remoteTaskId) || latest.status !== 'completed') {
    return null;
  }

  const finishedAt = toTime(latest.updatedAt);

  if (finishedAt === -Infinity) {
    return null;
  }

  return { createdAt: toTime(latest.createdAt), finishedAt, taskId: latest.remoteTaskId };
};

/** Closing the imports tray removes completed local tasks, which must not take the offer with them. */
export const isFinishedImportTaskDismissedFromTray = (
  finishedTask: FinishedImportTask,
  tasks: readonly ImportBatchOfferTask[],
): boolean =>
  tasks.every(
    (task) =>
      !task.remoteTaskId ||
      (task.remoteTaskId !== finishedTask.taskId && toTime(task.createdAt) <= finishedTask.createdAt),
  );
